import { useCallback, useState } from "react";
import type { DataTableSortDirection } from "components/common";
import {
  defaultDataTableGetSortValue,
  getNextDataTableSort,
  sortItemsByColumn,
  type DataTableGetSortValue,
} from "utils/dataTableSort";

export interface UseDataTableSortOptions<T> {
  initialColumnId?: string | null;
  initialDirection?: DataTableSortDirection;
  getSortValue?: DataTableGetSortValue<T>;
}

export function useDataTableSort<T>(options?: UseDataTableSortOptions<T>) {
  const [sortColumnId, setSortColumnId] = useState<string | null>(options?.initialColumnId ?? null);
  const [sortDirection, setSortDirection] = useState<DataTableSortDirection>(
    options?.initialDirection ?? "asc"
  );
  const getSortValue = options?.getSortValue ?? defaultDataTableGetSortValue;

  const onSortColumn = useCallback(
    (columnId: string) => {
      const next = getNextDataTableSort(sortColumnId, columnId, sortDirection);
      setSortColumnId(next.columnId);
      setSortDirection(next.direction);
    },
    [sortColumnId, sortDirection]
  );

  const sortItems = useCallback(
    (items: readonly T[]) => sortItemsByColumn(items, sortColumnId, sortDirection, getSortValue),
    [getSortValue, sortColumnId, sortDirection]
  );

  return {
    sortColumnId,
    sortDirection,
    onSortColumn,
    sortItems,
    setSortColumnId,
    setSortDirection,
  };
}
